import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  effect,
  Inject,
  Injectable,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
@Injectable({ providedIn: 'root' })
export class HeaderModeSwitcherService implements OnDestroy {
  readonly headerDefaultClass = 'default-header';
  readonly mustShowDefaultHeader = signal(true);

  private mutationObserver: MutationObserver;
  private renderer: Renderer2;

  constructor(
    private destroyRef: DestroyRef,
    private router: Router,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    if (isPlatformBrowser(this.platformId)) {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            !this.document.body.classList.contains(this.headerDefaultClass) &&
            this.mustShowDefaultHeader()
          ) {
            this.renderer.addClass(this.document.body, this.headerDefaultClass);
          }
        });
      });

      this.mutationObserver.observe(this.document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.applyHeaderClass();
        });
    }

    effect(() => {
      this.applyHeaderClass();
    });
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  changeMode(isDefault: boolean): void {
    this.mustShowDefaultHeader.set(isDefault);
  }

  private applyHeaderClass(): void {
    this.renderer[this.mustShowDefaultHeader() ? 'addClass' : 'removeClass'](
      this.document.body,
      this.headerDefaultClass,
    );
  }
}
